import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepProps } from '../types';
import {
  GenerateSuitabilityReportPayload,
  useGenerateSuitabilityReport,
} from '../hooks/useGenerateSuitabilityReport';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';

type ProcessingStepProps = StepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function ProcessingStep({ data, setData, onSubmittingChange }: ProcessingStepProps) {
  const { generateSuitabilityReport } = useGenerateSuitabilityReport();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submitReport = async () => {
    if (!data.template) {
      notifyAndLogError(
        'No template selected',
        'We don’t seem to have a template selected for this report. This isn’t expected to happen.\n' +
          'Please try again, and contact support if the issue persists.',
        new Error('Could not generate report due to missing template'),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: GenerateSuitabilityReportPayload = {
        reportTemplate: data.template.id,
        supportingDataUploadFileIds: data.adviceFiles.map(f => f.id),
        feeDocumentFileIds: data.feeDocuments.map(f => f.id),
        useDeepThinking: data.optimiseAdviceReadability,
        reportFormat: data.reportFormat,
        appendixFileIds: data.supportDocuments.map(u => u.fileId),
      };

      let response;
      if (data.isFamily) {
        if (!data.family) {
          notifyAndLogError(
            'No family has been selected',
            'We don’t seem to have a family selected for this report. This isn’t expected to happen.\n' +
              'Please try again, and contact support if the issue persists.',
            new Error('Could not generate report due to missing family ID'),
          );
          return;
        }
        response = await generateSuitabilityReport(data.family.id, 'family', payload);
      } else {
        if (!data.client?.id) {
          notifyAndLogError(
            'No client has been selected',
            'We don’t seem to have a client selected for this report. This isn’t expected to happen.\n' +
              'Please try again, and contact support if the issue persists.',
            new Error('Could not generate report due to missing client ID'),
          );
          return;
        }
        response = await generateSuitabilityReport(data.client.id, 'client', payload);
      }
      setData(prev => ({ ...prev, fileOwnerClientId: response.data.fileOwnerClientId }));
      dispatchAppEvent('SUITABILITY_REPORT_GENERATED');
    } catch (error) {
      notifyAndLogError('Failed to generate report', 'Please try again.', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasId = () => {
    if (data.isFamily) {
      return !!data.family?.id;
    } else {
      return !!data.client?.id;
    }
  };

  useEffect(() => {
    if (!hasId() || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submitReport();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
